type IDataCacheItemId = number | string;

/**
 * Элемент кэша.
 */
interface IDataCacheItem<TData = unknown> {
    /**
     * Хранимые данные.
     */
    data: TData;
    /**
     * Время в миллисекундах с начала эпохи, до наступления которого данные хранятся в кэше.
     * Значение `null` указывает, что время хранения данных не ограничено.
     */
    expireTime: number | null;
}

export interface IDataCacheInitProps {
    /**
     * Интервал времени в миллисекундах, по прошествии которого осуществляется очистка кэша от устаревших элементов.
     *
     * Отрицательное значение указывает, что интервал не задан.
     */
    clearInterval?: number;
    /**
     * Значение по умолчанию для "времени жизни" элемента данных в кэше.
     *
     * Отрицательное значение указывает, что "время жизни" не ограничено.
     */
    lifeTime?: number;
    /**
     * Ограничение на размер кэша.
     *
     * Отрицательное значение указывает, что размер кэша не ограничен.
     */
    sizeLimit?: number;
}

/**
 * Класс объектов для хранения в памяти временных данных.
 */
export class DataCache<TData = unknown> {
    /**
     * Интервал времени в миллисекундах, по прошествии которого осуществляется очистка кэша от устаревших элементов.
     *
     * Отрицательное значение указывает, что интервал не задан.
     */
    private clearInterval = -1;

    /**
     * Идентификатор таймаута, по завершению которого осуществляется удаление устаревших элементов.
     */
    private clearTimeoutId: number | null = null;

    /**
     * Кэш данных.
     */
    private data: Map<IDataCacheItemId, IDataCacheItem<TData>> = new Map();

    /**
     * Значение по умолчанию для "времени жизни" элемента данных в кэше.
     *
     * Отрицательное значение указывает, что "время жизни" не ограничено.
     */
    private lifeTime = -1;

    /**
     * Ограничение на размер кэша.
     *
     * Отрицательное значение указывает, что размер кэша не ограничен.
     */
    private sizeLimit = -1;

    /**
     * Создает кэш с заданными характеристиками.
     *
     * @param [initProps]
     *      Определяет характеристики создаваемого кэша.
     */
    constructor(initProps: IDataCacheInitProps = {}) {
        if (typeof initProps.clearInterval === 'number') {
            this.setClearInterval(initProps.clearInterval);
        }
        if (typeof initProps.lifeTime === 'number') {
            this.setLifeTime(initProps.lifeTime);
        }
        if (typeof initProps.sizeLimit === 'number') {
            this.setSizeLimit(initProps.sizeLimit);
        }
    }

    /**
     * Возвращает интервал времени в миллисекундах, по прошествии которого осуществляется очистка кэша от устаревших элементов.
     *
     * @return
     *      Интервал времени в миллисекундах, по прошествии которого осуществляется очистка кэша от устаревших элементов.
     *      Отрицательное значение указывает, что интервал не задан.
     */
    getClearInterval() {
        return this.clearInterval;
    }

    /**
     * Задает интервал времени в миллисекундах, по прошествии которого осуществляется очистка кэша от устаревших элементов.
     *
     * @param interval
     *      Интервал времени в миллисекундах, по прошествии которого осуществляется очистка кэша от устаревших элементов.
     *      Отрицательное значение указывает, что интервал не задан.
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    setClearInterval(interval: number): this {
        this.clearInterval = interval;

        return this.scheduleClear();
    }

    /**
     * Возвращает "время жизни" элемента данных в кэше.
     *
     * @return
     *      "Время жизни" в миллисекундах элемента данных в кэше.
     *      Отрицательное значение указывает, что "время жизни" не ограничено.
     */
    getLifeTime() {
        return this.lifeTime;
    }

    /**
     * Задает "время жизни" элемента данных в кэше.
     *
     * @param lifeTime
     *      "Время жизни" в миллисекундах элемента данных в кэше.
     *      Отрицательное значение указывает, что "время жизни" не ограничено.
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    setLifeTime(lifeTime: number): this {
        this.lifeTime = lifeTime;

        return this;
    }

    /**
     * Возвращает ограничение на размер кэша.
     *
     * @return
     *      Ограничение на размер кэша.
     *      Отрицательное значение указывает, что размер кэша не ограничен.
     */
    getSizeLimit() {
        return this.sizeLimit;
    }

    /**
     * Задает ограничение на размер кэша.
     * Указанное ограничение применяется к текущему размеру кэша.
     *
     * @param sizeLimit
     *      Ограничение на размер кэша.
     *      Отрицательное значение указывает, что размер кэша не ограничен.
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    setSizeLimit(sizeLimit: number): this {
        const { data } = this;
        this.sizeLimit = sizeLimit;

        if (sizeLimit >= 0) {
            if (sizeLimit) {
                while (sizeLimit < data.size) {
                    this.remove(data.keys().next().value!);
                }
            } else {
                this.removeAll();
            }
        }

        return this;
    }

    /**
     * Возвращает количество элементов данных в кэше, включая те, что уже устарели.
     *
     * @return
     *      Количество элементов данных в кэше.
     */
    getSize() {
        return this.data.size;
    }

    /**
     * Проверяет, устарели ли указанные данные.
     *
     * @param item
     *      Элемент, используемый для хранения данных.
     * @return
     *      `true`, если данные устарели, `false`, если данные подлежат хранению.
     */
    private isItemObsolete(item: IDataCacheItem) {
        const { expireTime } = item;

        return typeof expireTime === 'number' && expireTime < Date.now();
    }

    /**
     * Удаляет элемент из кэша, если он устарел.
     *
     * @param item
     *      Проверяемый элемент.
     * @param itemId
     *      Идентификатор проверяемого элемента.
     */
    private removeObsoleteItem = (item: IDataCacheItem, itemId: IDataCacheItemId) => {
        if (this.isItemObsolete(item)) {
            this.data.delete(itemId);
        }
    };

    /**
     * Удаляет устаревшие данные.
     *
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    clear = (): this => {
        this.data.forEach(this.removeObsoleteItem);

        return this.scheduleClear();
    };

    /**
     * Отменяет запланированную очистку устаревших данных через заданное время.
     *
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    cancelClear(): this {
        if (this.clearTimeoutId) {
            clearTimeout(this.clearTimeoutId);
        }
        this.clearTimeoutId = null;

        return this;
    }

    /**
     * При необходимости устанавливает очистку устаревших данных через заданное время.
     *
     * @param [timeout=this.getClearInterval()]
     *      Интервал времени в миллисекундах, по прошествии которого надо выполнить очистку кэша от устаревших элементов.
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    scheduleClear(timeout?: number): this {
        const interval = typeof timeout === 'number'
            ? timeout
            : this.getClearInterval();
        this.cancelClear();
        if (interval >= 0 && this.getSize()) {
            this.clearTimeoutId = window.setTimeout(this.clear, interval);
        }

        return this;
    }

    /**
     * Удаляет данные для указанного идентификатора.
     *
     * @param id
     *      Идентификатор удаляемых данных.
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    remove(id: IDataCacheItemId): this {
        this.data.delete(id);
        if (this.getSize() === 0) {
            this.cancelClear();
        }

        return this;
    }

    /**
     * Удаляет все данные.
     *
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    removeAll(): this {
        this.data.clear();

        return this.cancelClear();
    }

    /**
     * Возвращает данные для указанного идентификатора.
     *
     * Если такие данные отсутствуют, то возвращается `undefined`.
     * Если данные есть, но они устарели, то данные удаляются из кэша.
     *
     * @param id
     *      Идентификатор запрашиваемых данных.
     * @return
     *      Данные, соответствующие идентификатору, или `undefined`, если данных нет или они устарели.
     */
    get(id: IDataCacheItemId): TData | undefined {
        let result;
        const item = this.data.get(id);
        if (item) {
            if (this.isItemObsolete(item)) {
                this.remove(id);
            }
            else {
                result = item.data;
            }
        }

        return result;
    }

    /**
     * Проверяет, есть ли не устаревшие данные для указанного идентификатора.
     *
     * @param id
     *      Идентификатор запрашиваемых данных.
     * @return
     *      `true`, если кэш содержит данные для указанного идентификатора и они не устарели,
     *      `false`, если данных нет или они устарели.
     */
    has(id: IDataCacheItemId) {
        const item = this.data.get(id);

        return Boolean(item) && !this.isItemObsolete(item as IDataCacheItem);
    }

    /**
     * Сохраняет данные для указанного идентификатора.
     *
     * @param id
     *      Идентификатор сохраняемых данных.
     * @param data
     *      Сохраняемые данные.
     * @param [lifeTime=this.getLifeTime()]
     *      "Время жизни" в миллисекундах данных в кэше.
     *      Отрицательное значение указывает, что "время жизни" не ограничено.
     * @return
     *      Ссылка на объект, для которого вызван данный метод (`this`).
     */
    set(id: IDataCacheItemId, data: TData, lifeTime?: number): this {
        const { sizeLimit } = this;
        if (sizeLimit === 0) {
            return this;
        }
        const dataMap = this.data;
        // Если кэш заполнен полностью, удаляем элемент, который был записан первым
        if (dataMap.size === sizeLimit && !dataMap.has(id)) {
            this.remove(dataMap.keys().next().value!);
        }

        const item = dataMap.get(id) || ({} as IDataCacheItem<TData>);
        const ttl = typeof lifeTime === 'number'
            ? lifeTime
            : this.getLifeTime();
        item.data = data;
        item.expireTime = ttl >= 0
            ? Date.now() + ttl
            : null;
        // Удаление перед сохранением требуется, чтобы записываемые данные оказались в конце очереди хранимых элементов
        dataMap.delete(id);
        dataMap.set(id, item);

        if (!this.clearTimeoutId) {
            this.scheduleClear();
        }

        return this;
    }

    /**
     * Освобождает/очищает/удаляет ресурсы, связанные с объектом.
     */
    dispose() {
        this.removeAll();
        this.data = new Map();
    }
}
